import { useQuery } from '@tanstack/react-query'
import { apiRequest } from '@/api/client'

/**
 * The Bestellvorschlag endpoint (CHG-2026-10-bestellvorschlag, design "API").
 *
 * The server returns raw figures only — stock, incoming and sales per date column and weekday;
 * everything derived from them (Gesamt, proposals, Ø Liefertage, the expected sale of a planned
 * Tagesgericht) is computed in `lib/order-proposal-math.ts`. Weekday arrays are indexed Monday = 0 …
 * Sunday = 6. Quantities are decimals (NUMERIC(12,3) stock, fractional usage
 * parts).
 */
export interface OrderProposalColumnMeta {
  from: string
  to: string
  days: number
  /** How often each weekday occurs in the column (D7). */
  weekdayCounts: Array<number>
}

export interface OrderProposalSales {
  total: number
  /** The total split by weekday; adds up to `total`. */
  byWeekday: Array<number>
}

export interface OrderProposalArticle {
  sku: string
  name: string
  categoryId: number | null
  categoryName: string | null
  mainCategoryId: number | null
  mainCategoryName: string | null
  mainCategorySort: number | null
  categorySort: number | null
  stockTracked: boolean
  /** `inventory_levels.available_qty`; null without a levels row. */
  onHand: number | null
  incoming: number | null
  /** One entry per requested date column, in order. */
  sales: Array<OrderProposalSales>
}

export interface OrderProposalPlannedDish {
  sku: string
  name: string
  /** The order days the dish is planned on, `YYYY-MM-DD`. */
  days: Array<string>
  onHand: number | null
  incoming: number | null
}

export interface OrderProposalSoldDish {
  sku: string
  name: string
  /** The days the dish was a Tagesgericht, `YYYY-MM-DD`. */
  days: Array<string>
  /** On today's daily menu as a Tagesgericht. */
  today: boolean
  onHand: number | null
  incoming: number | null
  /** Sales only on its Tagesgericht days, one entry per date column. */
  sales: Array<{ total: number }>
}

export interface OrderProposalTagesgerichte {
  mainCategoryId: number | null
  /** All Tagesgerichte together, per date column. */
  totals: Array<OrderProposalSales>
  /** Every order day with the number of Tagesgerichte planned on it (0 without a daily menu). */
  days: Array<{ date: string; dishes: number }>
  planned: Array<OrderProposalPlannedDish>
  sold: Array<OrderProposalSoldDish>
}

export interface OrderProposalReport {
  today: string
  /** The order days: the Liefertag, or a Liefertag range. */
  deliveryFrom: string
  deliveryTo: string
  columns: Array<OrderProposalColumnMeta>
  articles: Array<OrderProposalArticle>
  tagesgerichte: OrderProposalTagesgerichte
}

export interface OrderProposalParams {
  restaurantId: number
  /** The order days; a single Liefertag has `from === to`. */
  delivery: { from: string; to: string }
  columns: Array<{ from: string; to: string }>
}

export function buildOrderProposalParams(params: OrderProposalParams): string {
  const qs = new URLSearchParams({
    restaurantId: String(params.restaurantId),
    deliveryFrom: params.delivery.from,
    deliveryTo: params.delivery.to,
  })
  params.columns.forEach((column, i) => {
    qs.set(`from${i + 1}`, column.from)
    qs.set(`to${i + 1}`, column.to)
  })
  return qs.toString()
}

export function useOrderProposalQuery(params: OrderProposalParams | null) {
  const qs = params ? buildOrderProposalParams(params) : ''
  return useQuery({
    // The query string carries Standort, order days and both columns, so it is the whole key.
    queryKey: ['order-proposal', qs],
    queryFn: () =>
      apiRequest<OrderProposalReport>(
        `/api/v1/admin/reports/order-proposal?${qs}`,
      ),
    staleTime: 60_000,
    enabled: params != null,
  })
}
